import { AI_PROVIDERS, PROVIDER_MODELS, DEFAULT_AI_PROVIDERS } from './ai-providers';

export interface ProviderAvailability {
  provider: string;
  available: boolean;
  configured: boolean;
  error?: string;
  models?: Record<string, string>;
  defaultModel?: string;
}

export interface ProviderAvailabilityCheckResult {
  providers: ProviderAvailability[];
  availableProviders: string[];
  configuredProviders: string[];
  timestamp: string;
}

/**
 * Frontend provider availability checker
 * Checks configuration based on provided API keys
 */
export class FrontendProviderAvailabilityChecker {
  private apiKeys: Record<string, string | undefined>;

  constructor(apiKeys: Record<string, string | undefined>) {
    this.apiKeys = apiKeys;
  }

  /**
   * Check if a specific provider is configured (has API key)
   */
  isProviderConfigured(provider: string): boolean {
    switch (provider) {
      case AI_PROVIDERS.ANTHROPIC:
        return !!this.apiKeys.ANTHROPIC_API_KEY;
      case AI_PROVIDERS.OPENAI:
        return !!this.apiKeys.OPENAI_API_KEY;
      case AI_PROVIDERS.GROK:
        return !!this.apiKeys.GROK_API_KEY;
      case AI_PROVIDERS.GEMINI:
        return !!this.apiKeys.GEMINI_API_KEY;
      case AI_PROVIDERS.MISTRAL:
        return !!this.apiKeys.MISTRAL_API_KEY;
      case AI_PROVIDERS.DEEPSEEK:
        return !!this.apiKeys.DEEPSEEK_API_KEY;
      case AI_PROVIDERS.CLOUDFLARE:
        // In frontend, we assume Cloudflare AI is always available
        // since we can't check for the AI binding
        return true;
      default:
        return false;
    }
  }

  /**
   * Get availability status for a specific provider
   * In frontend, we can only check configuration, not actual availability
   */
  getProviderAvailability(provider: string): ProviderAvailability {
    const configured = this.isProviderConfigured(provider);
    const models = PROVIDER_MODELS[provider as keyof typeof PROVIDER_MODELS] || {};
    const defaultModel = DEFAULT_AI_PROVIDERS[provider as keyof typeof DEFAULT_AI_PROVIDERS];

    return {
      provider,
      available: configured,
      configured,
      models,
      defaultModel
    };
  }

  /**
   * Check availability for all providers
   */
  checkAllProviders(): ProviderAvailabilityCheckResult {
    const providers: ProviderAvailability[] = [];
    const availableProviders: string[] = [];
    const configuredProviders: string[] = [];

    for (const provider of Object.values(AI_PROVIDERS)) {
      const availability = this.getProviderAvailability(provider);
      providers.push(availability);

      if (availability.configured) {
        configuredProviders.push(provider);
        availableProviders.push(provider);
      }
    }

    return {
      providers,
      availableProviders,
      configuredProviders,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get all configured providers
   */
  getConfiguredProviders(): string[] {
    return Object.values(AI_PROVIDERS).filter(provider => 
      this.isProviderConfigured(provider)
    );
  }

  /**
   * Update API keys
   */
  updateApiKeys(apiKeys: Record<string, string | undefined>) {
    this.apiKeys = apiKeys;
  }
}

/**
 * Utility function to create a frontend provider availability checker
 */
export function createFrontendProviderChecker(
  apiKeys: Record<string, string | undefined>
): FrontendProviderAvailabilityChecker {
  return new FrontendProviderAvailabilityChecker(apiKeys);
}

/**
 * Check which providers need API keys to be configured
 */
export function getProvidersNeedingConfiguration(): string[] {
  // Cloudflare AI doesn't need configuration in frontend
  return Object.values(AI_PROVIDERS).filter(provider => provider !== AI_PROVIDERS.CLOUDFLARE);
}

/**
 * Get the display message for a provider's availability status
 */
export function getProviderStatusMessage(availability: ProviderAvailability): string {
  if (availability.provider === AI_PROVIDERS.CLOUDFLARE) {
    return 'Built-in (Always Available)';
  }
  
  if (availability.configured) {
    return 'API Key Configured';
  }
  
  return 'API Key Required';
}

/**
 * Check if at least one provider is available
 */
export function hasAvailableProvider(apiKeys: Record<string, string | undefined>): boolean {
  const checker = new FrontendProviderAvailabilityChecker(apiKeys);
  const configured = checker.getConfiguredProviders();
  // Always have at least Cloudflare AI available
  return configured.length > 0;
}